import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Temporal } from 'temporal-polyfill';

import { getCoachAvailabilitiesForCoach, getCoachClasses, getUser } from '@/lib/api-calls';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling';
import { QueryKeys } from '@/lib/queries';

import { InfoPage } from '@/components/info-page';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';

import { FrequentTimeZones, isAirAdmin } from '@airreading/common';
import { SortedDaysOfWeek } from '@airreading/common/data';
import { ClassesCard } from '../-components/classes-card';
import { ManagePermissionsDropdown } from '../-components/manage-permissions-dropdown';
import { coachesTabs } from '../-components/tabs';
import { TeachingAvailabilityCard } from '../-components/teaching-availability-card';
import { validateRouteRole } from '@/lib/routing';
import { ReassignedStudentGroupsCard } from '../-components/reassigned-student-groups-card';
import { paginationRequestSchema } from '@/lib/pagination';

export const Route = createFileRoute('/_layout/teachers/$teacherId/schedule')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: SchedulePage,
  validateSearch: paginationRequestSchema,
});

function SchedulePage() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load teacher. Please try again.',
        };
      }}
    >
      <ScheduleDetail />
    </InfoPageSuspenseBoundary>
  );
}

function ScheduleDetail() {
  const { teacherId: userId } = Route.useParams();
  const { data: user } = useSuspenseQuery({
    queryKey: QueryKeys.getUser(userId),
    queryFn: () => getUser(userId),
  });

  const { data: availabilities } = useSuspenseQuery({
    ...getCoachAvailabilitiesForCoach(userId),
  });

  // Groups this teacher was removed from are still current until their last remaining class.
  const { data: classes } = useSuspenseQuery({
    ...getCoachClasses(userId, true),
  });

  const filteredClasses = classes.filter((c) => (c.futureSessions ?? 0) > 0);

  // Sort classes by day of week and start time
  const sortedClasses = filteredClasses.toSorted((a, b) => {
    // Get the first meeting block for each class
    const aFirstBlock = a.classTime.meetingBlocks?.[0];
    const bFirstBlock = b.classTime.meetingBlocks?.[0];

    if (!aFirstBlock || !bFirstBlock) return 0;

    // Compare by day of week using SortedDaysOfWeek
    const dayDiff = SortedDaysOfWeek.indexOf(aFirstBlock.day) - SortedDaysOfWeek.indexOf(bFirstBlock.day);
    if (dayDiff !== 0) return dayDiff;

    // If same day, compare by start time
    return Temporal.PlainTime.compare(aFirstBlock.startPlainTime, bFirstBlock.startPlainTime);
  });

  return (
    <InfoPage
      headerText={user.fullNameOrEmail}
      headerActions={<ManagePermissionsDropdown user={user} />}
      tabs={coachesTabs({ teacherId: userId })}
      breadcrumbs={{
        linkItems: [
          {
            label: 'Teachers',
            link: {
              to: '/teachers',
            },
          },
        ],
        currentPage: user.fullNameOrEmail ?? '',
      }}
    >
      <TeachingAvailabilityCard availabilities={availabilities} timeZone={FrequentTimeZones.UsCentral} />
      <ClassesCard classes={sortedClasses} />
      <ReassignedStudentGroupsCard coachId={userId} route={Route} />
    </InfoPage>
  );
}
